import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { GetStaffId } from "./utils";
import { jwtDecode } from "jwt-decode";
import { apiClient, queryClient } from "./api";
import useAuth from "../hooks/useAuth";

function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";
  const { setAuth } = useAuth();

  return useMutation({
    mutationFn: (formData) => {
      const loginUrl = formData.isAzureADLogin
        ? "/users/auth/azure"
        : "/users/login";
      return apiClient.post(loginUrl, formData, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
    },
    onSuccess: (res) => {
      const token = res.data.token;
      const decodedToken = jwtDecode(token);
      const permissions = decodedToken.permissions;
      const staff_id = decodedToken.staff_id;

      // Store the token in localStorage
      localStorage.setItem("token", token);

      setAuth({ staff_id, permissions, token });

      queryClient.invalidateQueries("userdata");

      navigate(from, { replace: true });
    },
    onError: (error) => {
      console.error("An error occurred during login:", error);
      if (error.response && error.response.status === 500) {
        console.error("An error occurred during login:", error);
        navigate("/error");
      }
    },
  });
}

function getStaffIdFromLocalStorage() {
  const authState = localStorage.getItem("authState");
  if (authState) {
    const parsedAuthState = JSON.parse(authState);
    return parsedAuthState.staff_id;
  }
  return null;
}

function UseUserinfo() {
  const { auth } = useAuth();
  const staffId = auth?.staff_id;
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["userdata", staffId],
    queryFn: async () => {
      if (!staffId) {
        return null;
      }
      const res = await apiClientPrivate.get(`users/${staffId}`);
      return res.data;
    },
    staleTime: 100000000,
  });
}

function useTotalApplications() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["totalApplications"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/total");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useVacancyDetails() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["details"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/vacancy/details");
      return res.data;
    },
    staleTime: 300000,
  });
}

function getVacancies() {
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["vacancies"],
    queryFn: async () => {
      const response = await apiClientPrivate.get("/vacancy");
      const formattedData = response.data.map((vacancy) => ({
        ...vacancy,
        opening_date: new Date(vacancy.opening_date).toLocaleDateString(),
        closing_date: new Date(vacancy.closing_date).toLocaleDateString(),
      }));
      return formattedData;
    },
    // staleTime: 300000,
  });
}

function useSendNotificationsToApplicants() {
  const apiClientPrivate = useAxiosPrivate();

  return useMutation({
    mutationFn: async (formData) => {
      const res = await apiClientPrivate.post(
        "vacancy/notifications",
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("vacancies");
    },
  });
}

function getAllusers() {
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["allusers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user");
      return res.data;
    },
  });
}

function useGetUserRoles() {
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["userRoles"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/roles");
      return res.data;
    },
    staleTime: 1000 * 60 * 10,
  });
}

function getLocationBu() {
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["details"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/vacancy/detais");
      return res.data;
    },
    staleTime: 300000,
  });
}

const processRowUpdate = async (newRow) => {
  const updatedRow = {
    ...newRow,
    isNew: false,
    description: getRoleDescription(newRow.role),
  };

  try {
    // Validate all required fields
    if (
      !newRow.staffid ||
      !newRow.fullname ||
      !newRow.sbucode ||
      !newRow.role ||
      !newRow.departmentcode
    ) {
      setSnackbar({
        children: "All fields must be filled except description",
        severity: "error",
      });
      return newRow; // Don't proceed with the request if validation fails
    }

    const res = await apiClientPrivate.put(
      `/user/add/${newRow.id}`,
      updatedRow,
    );

    if (res.status === 200) {
      setSnackbar({
        children: `User successfully ${newRow.isNew ? "created" : "updated"}`,
        severity: "success",
      });
      setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
      return updatedRow;
    } else {
      setSnackbar({
        children: `Failed to ${newRow.isNew ? "create" : "update"} user`,
        severity: "error",
      });
    }
  } catch (error) {
    console.error("Error:", error);
    setSnackbar({
      children: `An error occurred while ${newRow.isNew ? "creating" : "updating"
        } user`,
      severity: "error",
    });
  }

  return newRow; // Return the original row if the update failed
};

function useTotalVacancies() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["totalVacancies"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/vacancy/total");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useOpenVacancies() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["openVacancies"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/vacancy/open");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useClosedVacancies() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["closedVacancies"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/vacancy/closed");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function GetAllCourses() {
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/vacancy/courses");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function UpdateCourse(id, updatedCourse) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["updatecourse", id, updatedCourse],
    queryFn: async () => {
      if (!id || !updatedCourse) {
        return null;
      }
      const res = await apiClientPrivate.put(
        `/vacancy/vacancy/courses/${id}`,
      );
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useDeleteCourse() {
  const apiClientPrivate = useAxiosPrivate();

  const deleteCourse = async (id) => {
    try {
      await apiClientPrivate.delete(`/vacancy/vacancy/courses/${id}`);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        throw new Error("Course not found");
      }
      console.error("Error deleting course:", error);
      throw error;
    }
  };

  return deleteCourse;
}

function useTotalUsers() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["totalUsers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/total");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useViewUsers() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["viewUsers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/view");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useVerifyUsers() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["verifyUsers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/verify");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useApproveUsers() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["approveUsers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/approve");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useHeadUsers() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["headUsers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/head");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useAdminUsers() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["adminUsers"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/user/admin");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useGetAllApplicationInformation(internship_type) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "all"],
    queryFn: async () => {
      const res = await apiClientPrivate.get(
        `/info/applications/${internship_type}`,
      );
      return res.data;
    },
  });
}

const useViewAllApplicationInformation = (params) => {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "all", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/applications/view", {
        params,
      });
      return res.data;
    },
  });
};

function useGetAcceptedApplications(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["accepted", "all", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/applications/accepted", {
        params,
      });
      return res.data;
    },
  });
}

function useGetInterns(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["interns", "all", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/interns", { params });
      return res.data;
    },
  });
}

function useGetMyInterns(params) {
  const staffId = getStaffIdFromLocalStorage();
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["my-interns", staffId, params],
    queryFn: async () => {
      const res = await apiClientPrivate.get(`/info/myinterns/${staffId}`, {
        params,
      });
      return res.data;
    },
    enabled: !!staffId,
  });
}

function useGetInternByid(id) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["intern", id, "intern information"],
    queryFn: async () => {
      if (!id) {
        return null;
      }
      const res = await apiClientPrivate.get(`info/intern/${id}`);
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useGetAssessmentDetails(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["assessment", params.intern_id, params.month_year],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/assessments/details", {
        params,
      });
      return res.data;
    },
    enabled: !!params.intern_id && !!params.month_year,
  });
}

function useSubmitAssessment() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await apiClientPrivate.post(
        "/api/assessments/submit",
        payload,
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["assessment"]);
      queryClient.invalidateQueries(["interns"]);
      queryClient.invalidateQueries(["manager-pending"]);
      queryClient.invalidateQueries(["my-interns"]);
    },
  });
}

function useUpdateInternBankDetails() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async ({ intern_id, ...data }) => {
      const res = await apiClientPrivate.put(
        `/staff/interns/${intern_id}/bank`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["interns"]);
    },
  });
}

function useGetBusinessUnits(businessId) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["businessUnits", businessId ?? null],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/business-units", {
        params: businessId ? { businessId } : {},
      });
      return res.data;
    },
    staleTime: 1000 * 60 * 60,
  });
}

function useGetAllDepartments() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["allDepartments"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/departments");
      return res.data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}

function useGetAllSupervisors() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["allSupervisors"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/staff/supervisors");
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

function useGetStaffByUnit(business_unit_code) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["staff", "unit", business_unit_code],
    queryFn: async () => {
      const res = await apiClientPrivate.get(`/staff`, {
        params: { business_unit_code },
      });
      return res.data;
    },
    enabled: !!business_unit_code,
  });
}

function useExternalStaffLookup(params) {
  const apiClientPrivate = useAxiosPrivate();
  const staffId = params?.staffId?.trim();
  const name = params?.name?.trim();

  return useQuery({
    queryKey: ["external-staff-lookup", { staffId, name }],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/staff/external-lookup", {
        params: {
          ...(staffId ? { staffId } : {}),
          ...(name ? { name } : {}),
        },
      });
      return res.data;
    },
    enabled: Boolean(staffId || name),
    keepPreviousData: true,
  });
}

function useAssignSupervisor() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async ({ internId, payload }) => {
      const res = await apiClientPrivate.put(
        `/staff/assign-supervisor/${internId}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["intern"]);
      queryClient.invalidateQueries(["interns"]);
    },
  });
}

function useResetAttendanceDevice() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (internId) => {
      const response = await apiClientPrivate.post(
        `info/intern/${internId}/reset-device`,
      );
      return response.data;
    },
    onSuccess: (data, internId) => {
      queryClient.invalidateQueries(["intern", internId]);
      toast.success("Attendance device has been successfully reset.");
    },
    onError: (error) => {
      toast.error("Failed to reset attendance device.");
      console.error("Error resetting attendance device:", error);
    },
  });
}

function useVerifyAllApplicationInformation(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "verify", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/applications/verifyall", {
        params,
      });
      return res.data;
    },
  });
}

function useApproveAllApplicationInformation(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "approve", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get(
        "/info/applications/approveall",
        {
          params,
        },
      );
      return res.data;
    },
  });
}

function useAcceptAllApplicationInformation(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "accept", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/applications/acceptall", {
        params,
      });
      return res.data;
    },
  });
}

function useGetAllinformationByid({ application_id }) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", application_id, "application information"],
    queryFn: async () => {
      if (!application_id) {
        return null;
      }
      const res = await apiClientPrivate.get(
        `info/application/${application_id}`,
      );
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useGetDepartmentsByBusinessUnit({ business_unit_id }) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["departments", business_unit_id, "BU departments"],
    queryFn: async () => {
      if (!business_unit_id) {
        return null;
      }
      const res = await apiClientPrivate.get(
        `info/departments/${business_unit_id}`,
      );
      return res.data;
    },
  });
}

function useGetDocumentRequired(vacancy_id) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", vacancy_id, "documents types"],
    queryFn: async () => {
      if (!vacancy_id) {
        return null;
      }
      try {
        const res = await apiClientPrivate.get(
          `info/documents/types/${vacancy_id}`,
        );

        return res.data;
      } catch (error) {
        handleError(error);
      }
    },
    staleTime: 100000000,
  });
}

function useGetAcceptedbycode(vacancy_code) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["vacancy", vacancy_code, "accepted application"],
    queryFn: async () => {
      if (!vacancy_code) {
        return null;
      }
      const res = await apiClientPrivate.get(
        `info/application/vacancy/${vacancy_code}`,
      );
      return res.data;
    },
    enabled: !!vacancy_code,
    staleTime: 300000,
  });
}

function useViewAllinformationByid({ application_id }) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", application_id, "application information"],
    queryFn: async () => {
      if (!application_id) {
        return null;
      }
      const res = await apiClientPrivate.get(
        `info/application/view/${application_id}`,
      );
      return res.data;
    },
  });
}

function usePostVerifyRejectApplicationById() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async ({ formData }) => {
      const response = await apiClientPrivate.post(`info/status`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("application");
    },
  });
}

function usePostAcceptRejectApplicationById() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async ({ formData }) => {
      const response = await apiClientPrivate.post(
        `info/finalstatus`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("application");
    },
  });
}

function usePostMailApplicationById() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async ({ formData }) => {
      const response = await apiClientPrivate.post(
        `info/acceptmail`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("application");
    },
  });
}

function useGetVerifiedApplications(internship_type) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "verified"],
    queryFn: async () => {
      const res = await apiClientPrivate.get(
        `/info/applications/admin/${internship_type}`,
      );
      return res.data;
    },
  });
}

function useGetApprovedApplications(internship_type) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["application", "approved"],
    queryFn: async () => {
      const res = await apiClientPrivate.get(
        `/info/applications/final/${internship_type}`,
      );
      return res.data;
    },
  });
}

function useSubmitMonthlyClearance() {
  const apiClientPrivate = useAxiosPrivate();

  return useMutation({
    mutationFn: async (payload) => {
      const res = await apiClientPrivate.post(
        "/info/monthly-assesment",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["interns"]);
    },
    onError: (error) => {
      console.error("Submission failed", error);
    },
  });
}

function useBulkVerifyApplications() {
  const queryClient = useQueryClient();
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClientPrivate.post(
        "/info/applications/bulk-verify",
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["verify-applications"]);
    },
  });
}

function useBulkApproveApplications() {
  const queryClient = useQueryClient();
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClientPrivate.post(
        "/info/applications/bulk-approve",
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["application"]);
    },
  });
}

function useValidateBulkAcceptance() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await apiClientPrivate.post(
        "/info/applications/validate-bulk",
        payload,
      );
      return res.data;
    },
  });
}

function useBulkAcceptApplications() {
  const queryClient = useQueryClient();
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await apiClientPrivate.post(
        "/info/applications/bulk-accept",
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["application"]);
      queryClient.invalidateQueries(["accepted"]);
      queryClient.invalidateQueries(["approved"]);
    },
  });
}

function useGetInternsForSupervisor() {
  const authState = JSON.parse(localStorage.getItem("authState"));
  const staffId = authState ? authState.staff_id : null;

  const fetchInterns = async () => {
    if (!staffId) {
      throw new Error("No staff ID found");
    }
    const response = await axios.get(
      `/intern-supervisor-information/${staffId}`,
    );
    return response.data;
  };

  return useQuery({
    queryKey: ["internforSupervisor", staffId],
    queryFn: fetchInterns,
    onSuccess: (data) => {
      console.log("Successfully fetched interns for supervisor", data);
    },
    onError: (error) => {
      console.error("Error fetching interns for supervisor", error);
    },
  });
}

function useGetManagerPendingAssessments(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["manager-pending", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get(
        "/api/assessments/manager-pending",
        {
          params,
        },
      );
      return res.data;
    },
    enabled: !!params?.month_year,
  });
}

export function useBulkEndorseAssessments() {
  const queryClient = useQueryClient();
  const apiClientPrivate = useAxiosPrivate();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClientPrivate.post(
        "/api/assessments/manager-bulk-endorse",
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["manager-pending"]);
    },
  });
}

function useEndorseAssessment() {
  const apiClientPrivate = useAxiosPrivate();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await apiClientPrivate.post(
        "/api/assessments/endorse",
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["manager-pending"]);
      queryClient.invalidateQueries(["assessment"]);
    },
  });
}

function useGetPaymentAdvice(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["payment-advice", params],
    queryFn: async () => {
      try {
        const res = await apiClientPrivate.get("/api/payment-advice", {
          params,
        });
        return res.data;
      } catch (error) {
        console.error("Error fetching payment advice:", error);
        throw error;
      }
    },
    enabled: !!params?.month && !!params?.year,
  });
}

function useGetSecurityDashboardData(params) {
  const apiClientPrivate = useAxiosPrivate();

  return useQuery({
    queryKey: ["security-dashboard", params],
    queryFn: async () => {
      try {
        const res = await apiClientPrivate.get("/staff/security-dashboard", {
          params,
        });
        return res.data;
      } catch (error) {
        console.error("Error fetching security dashboard:", error);
        throw error;
      }
    },
    keepPreviousData: true,
  });
}

function usePipelineSummary(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "pipeline", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/pipeline", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
        },
      });
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useApplicationTrends(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "trends", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/trends", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
          interval: "day",
        },
      });
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useDemographics(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "demographics", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/demographics", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
        },
      });
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useFinancialStats(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "financials", params],
    queryFn: async () => {
      let month, year;
      if (params?.to) {
        const d = new Date(params.to);
        month = d.getMonth() + 1;
        year = d.getFullYear();
      } else {
        const now = new Date();
        month = now.getMonth() + 1;
        year = now.getFullYear();
      }

      const res = await apiClientPrivate.get("/api/analytics/financials", {
        params: { month, year },
      });
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useDetailedPipeline(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "detailed-pipeline", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get(
        "/api/analytics/detailed-pipeline",
        {
          params: {
            start_date: params?.from,
            end_date: params?.to,
          },
        },
      );
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useDashboardData(params, enabled = true) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "dashboard", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/dashboard", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
        },
      });
      return res.data;
    },
    enabled,
    keepPreviousData: true,
  });
}

function useDeploymentStats(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "deployment", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/deployment", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
        },
      });
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useSupervisorStats(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "supervisor", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/supervisor", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
        },
      });
      return res.data;
    },
    keepPreviousData: true,
  });
}

function useSecurityStats(params) {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["analytics", "security", params],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/api/analytics/security", {
        params: {
          start_date: params?.from,
          end_date: params?.to,
        },
      });
      return res.data;
    },
    refetchInterval: 30000,
    keepPreviousData: true,
  });
}

function useComplianceModules() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["compliance-modules"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/compliance-modules");
      return res.data;
    },
    // staleTime: 300000,
  });
}

function useCreateComplianceModule() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      const res = await apiClientPrivate.post(
        "/info/compliance-modules",
        formData,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["compliance-modules"]);
    },
  });
}

function useUpdateComplianceModule() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const res = await apiClientPrivate.put(
        `/info/compliance-modules/${id}`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["compliance-modules"]);
    },
  });
}

function useToggleComplianceModule() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClientPrivate.delete(
        `/info/compliance-modules/${id}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["compliance-modules"]);
    },
  });
}

function useOfficeBuildings() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["office-buildings"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/office-buildings");
      return res.data;
    },
  });
}

function useCompanyLocations() {
  const apiClientPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ["company-locations"],
    queryFn: async () => {
      const res = await apiClientPrivate.get("/info/company-locations");
      return res.data;
    },
    staleTime: Infinity,
  });
}

function useCreateOfficeBuilding() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      const res = await apiClientPrivate.post(
        "/info/office-buildings",
        formData,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["office-buildings"]);
    },
  });
}

function useUpdateOfficeBuilding() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const res = await apiClientPrivate.put(
        `/info/office-buildings/${id}`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["office-buildings"]);
    },
  });
}

function useDeleteOfficeBuilding() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await apiClientPrivate.delete(`/info/office-buildings/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["office-buildings"]);
    },
  });
}

function useUpdateInternPlacement() {
  const apiClientPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ internId, office_building_id }) => {
      const res = await apiClientPrivate.put(
        `/staff/interns/${internId}/placement`,
        {
          office_building_id,
        },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["my-interns"]);
    },
  });
}

async function getManagerDetails(id) {
  const response = await apiClient.get(`/users/${id}`);
  return response.data;
}

export {
  getManagerDetails,
  useLogin,
  UseUserinfo,
  // Useinterninfo,
  useTotalApplications,
  useGetDocumentRequired,
  useSubmitMonthlyClearance,
  useGetInternsForSupervisor,
  usePostVerifyRejectApplicationById,
  usePostAcceptRejectApplicationById,
  useGetAllinformationByid,
  useGetDepartmentsByBusinessUnit,
  useGetAllApplicationInformation,
  useGetVerifiedApplications,
  useViewAllApplicationInformation,
  useViewAllinformationByid,
  useTotalVacancies,
  useOpenVacancies,
  useClosedVacancies,
  useSendNotificationsToApplicants,
  GetAllCourses,
  getVacancies,
  useTotalUsers,
  useViewUsers,
  useVerifyUsers,
  useApproveUsers,
  useHeadUsers,
  useAdminUsers,
  getAllusers,
  useGetUserRoles,
  processRowUpdate,
  useGetApprovedApplications,
  usePostMailApplicationById,
  getLocationBu,
  useVacancyDetails,
  useVerifyAllApplicationInformation,
  useApproveAllApplicationInformation,
  useAcceptAllApplicationInformation,
  useGetAcceptedbycode,
  useGetAcceptedApplications,
  useGetInterns,
  useGetMyInterns,
  UpdateCourse,
  useDeleteCourse,
  useGetInternByid,
  useSubmitAssessment,
  useGetManagerPendingAssessments,
  useEndorseAssessment,
  useUpdateInternBankDetails,
  useGetAssessmentDetails,
  useResetAttendanceDevice,
  useGetPaymentAdvice,
  useGetBusinessUnits,
  useGetAllDepartments,
  useGetAllSupervisors,
  useGetStaffByUnit,
  useExternalStaffLookup,
  useAssignSupervisor,
  useBulkVerifyApplications,
  useBulkApproveApplications,
  useBulkAcceptApplications,
  useValidateBulkAcceptance,
  useGetSecurityDashboardData,
  usePipelineSummary,
  useApplicationTrends,
  useDemographics,
  useFinancialStats,
  useDetailedPipeline,
  useDeploymentStats,
  useSupervisorStats,
  useSecurityStats,
  useComplianceModules,
  useCreateComplianceModule,
  useUpdateComplianceModule,
  useToggleComplianceModule,
  useOfficeBuildings,
  useCompanyLocations,
  useCreateOfficeBuilding,
  useUpdateOfficeBuilding,
  useDeleteOfficeBuilding,
  useUpdateInternPlacement,
  useDashboardData,
};
